import { useMemo, useState } from 'react';
import { Archive, Plus } from 'lucide-react';
import { useArchivePromotion, useCreatePromotion, usePromotions } from '@/hooks/usePromotions';
import { SearchableSelect } from '@components/common/SearchableSelect';
import type { Promotion, PromotionCreatePayload, PromotionDuration, PromotionKind } from '@/types/promote';

type PromotionFormState = {
  code: string;
  display_name: string;
  description: string;
  kind: PromotionKind;
  trial_extension_days: string;
  discount_percent: string;
  discount_fixed_cents: string;
  duration: PromotionDuration;
  duration_in_months: string;
  starts_at: string;
  ends_at: string;
  max_redemptions: string;
};

const EMPTY_PROMOTION_FORM: PromotionFormState = {
  code: '',
  display_name: '',
  description: '',
  kind: 'percent_discount',
  trial_extension_days: '',
  discount_percent: '',
  discount_fixed_cents: '',
  duration: 'once',
  duration_in_months: '',
  starts_at: '',
  ends_at: '',
  max_redemptions: '',
};

const PROMOTION_KIND_OPTIONS = [
  { id: 'percent_discount', label: 'Percent discount' },
  { id: 'fixed_discount', label: 'Fixed discount' },
  { id: 'trial_extension', label: 'Trial extension' },
  { id: 'custom', label: 'Custom' },
];

const PROMOTION_DURATION_OPTIONS = [
  { id: 'once', label: 'Once' },
  { id: 'forever', label: 'Forever' },
  { id: 'repeating', label: 'Repeating' },
];

function optionalNumber(value: string) {
  const trimmed = value.trim();
  return trimmed ? Number(trimmed) : null;
}

function optionalDate(value: string) {
  return value ? new Date(`${value}T12:00:00Z`).toISOString() : null;
}

export function summarizePromotion(promotion: Promotion) {
  if (promotion.kind === 'percent_discount' && promotion.discount_percent != null) {
    return `${promotion.discount_percent}% off`;
  }
  if (promotion.kind === 'fixed_discount' && promotion.discount_fixed_cents != null) {
    return `$${(promotion.discount_fixed_cents / 100).toFixed(2)} off`;
  }
  if (promotion.kind === 'trial_extension' && promotion.trial_extension_days != null) {
    return `+${promotion.trial_extension_days} trial days`;
  }
  return promotion.display_name;
}

export function PromotionCatalogPanel() {
  const promotions = usePromotions();
  const createPromotion = useCreatePromotion();
  const archivePromotion = useArchivePromotion();
  const [formState, setFormState] = useState<PromotionFormState>(EMPTY_PROMOTION_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const activePromotions = useMemo(
    () => (promotions.data ?? []).filter((promotion) => promotion.is_active),
    [promotions.data],
  );

  async function handleCreate() {
    setFormError(null);

    if (!formState.code.trim() || !formState.display_name.trim()) {
      setFormError('Code and display name are required.');
      return;
    }
    if (formState.kind === 'percent_discount' && !formState.discount_percent.trim()) {
      setFormError('Percent discount promotions need a percent value.');
      return;
    }
    if (formState.kind === 'fixed_discount' && !formState.discount_fixed_cents.trim()) {
      setFormError('Fixed discount promotions need an amount in cents.');
      return;
    }
    if (formState.kind === 'trial_extension' && !formState.trial_extension_days.trim()) {
      setFormError('Trial extension promotions need a day count.');
      return;
    }

    const payload: PromotionCreatePayload = {
      code: formState.code.trim().toUpperCase(),
      display_name: formState.display_name.trim(),
      description: formState.description.trim() || null,
      kind: formState.kind,
      duration: formState.duration,
      duration_in_months: optionalNumber(formState.duration_in_months),
      starts_at: optionalDate(formState.starts_at),
      ends_at: optionalDate(formState.ends_at),
      max_redemptions: optionalNumber(formState.max_redemptions),
      trial_extension_days:
        formState.kind === 'trial_extension' ? optionalNumber(formState.trial_extension_days) : null,
      discount_percent:
        formState.kind === 'percent_discount' ? optionalNumber(formState.discount_percent) : null,
      discount_fixed_cents:
        formState.kind === 'fixed_discount' ? optionalNumber(formState.discount_fixed_cents) : null,
    };

    await createPromotion.mutateAsync(payload);
    setFormState(EMPTY_PROMOTION_FORM);
  }

  const mutationError =
    createPromotion.error instanceof Error
      ? createPromotion.error.message
      : archivePromotion.error instanceof Error
      ? archivePromotion.error.message
      : null;

  return (
    <div className="detail-form">
      <div className="integration-status-row">
        <span className="pill pill-medium">{activePromotions.length} active</span>
        <span className="muted-copy">
          Promotion codes can be applied during CRM account promotion and public email verification.
        </span>
      </div>

      <div className="promotion-create-form">
        <div className="form-grid">
          <label className="form-field">
            <span>Code</span>
            <input
              value={formState.code}
              onChange={(event) => setFormState((current) => ({ ...current, code: event.target.value }))}
              placeholder="WELCOME10"
            />
          </label>
          <label className="form-field">
            <span>Display name</span>
            <input
              value={formState.display_name}
              onChange={(event) => setFormState((current) => ({ ...current, display_name: event.target.value }))}
              placeholder="Welcome 10%"
            />
          </label>
          <label className="form-field">
            <span>Kind</span>
            <SearchableSelect
              options={PROMOTION_KIND_OPTIONS}
              value={formState.kind}
              onChange={(value) =>
                setFormState((current) => ({ ...current, kind: value as PromotionKind }))
              }
              placeholder="Promotion kind"
              clearable={false}
            />
          </label>
          <label className="form-field">
            <span>Duration</span>
            <SearchableSelect
              options={PROMOTION_DURATION_OPTIONS}
              value={formState.duration}
              onChange={(value) =>
                setFormState((current) => ({ ...current, duration: value as PromotionDuration }))
              }
              placeholder="Promotion duration"
              clearable={false}
            />
          </label>

          {formState.kind === 'percent_discount' ? (
            <label className="form-field">
              <span>Percent off</span>
              <input
                type="number"
                min="0"
                max="100"
                value={formState.discount_percent}
                onChange={(event) =>
                  setFormState((current) => ({ ...current, discount_percent: event.target.value }))
                }
              />
            </label>
          ) : null}

          {formState.kind === 'fixed_discount' ? (
            <label className="form-field">
              <span>Amount off (cents)</span>
              <input
                type="number"
                min="0"
                value={formState.discount_fixed_cents}
                onChange={(event) =>
                  setFormState((current) => ({ ...current, discount_fixed_cents: event.target.value }))
                }
              />
            </label>
          ) : null}

          {formState.kind === 'trial_extension' ? (
            <label className="form-field">
              <span>Trial days</span>
              <input
                type="number"
                min="0"
                value={formState.trial_extension_days}
                onChange={(event) =>
                  setFormState((current) => ({ ...current, trial_extension_days: event.target.value }))
                }
              />
            </label>
          ) : null}

          {formState.duration === 'repeating' ? (
            <label className="form-field">
              <span>Duration months</span>
              <input
                type="number"
                min="1"
                value={formState.duration_in_months}
                onChange={(event) =>
                  setFormState((current) => ({ ...current, duration_in_months: event.target.value }))
                }
              />
            </label>
          ) : null}

          <label className="form-field">
            <span>Starts</span>
            <input
              type="date"
              value={formState.starts_at}
              onChange={(event) => setFormState((current) => ({ ...current, starts_at: event.target.value }))}
            />
          </label>
          <label className="form-field">
            <span>Ends</span>
            <input
              type="date"
              value={formState.ends_at}
              onChange={(event) => setFormState((current) => ({ ...current, ends_at: event.target.value }))}
            />
          </label>
          <label className="form-field">
            <span>Max redemptions</span>
            <input
              type="number"
              min="1"
              value={formState.max_redemptions}
              onChange={(event) =>
                setFormState((current) => ({ ...current, max_redemptions: event.target.value }))
              }
            />
          </label>
          <label className="form-field form-field-wide">
            <span>Description</span>
            <textarea
              rows={2}
              value={formState.description}
              onChange={(event) => setFormState((current) => ({ ...current, description: event.target.value }))}
            />
          </label>
        </div>

        {formError || mutationError ? <p className="form-error">{formError || mutationError}</p> : null}

        <div className="form-actions">
          <button
            type="button"
            className="button button-primary"
            disabled={createPromotion.isPending}
            onClick={() => void handleCreate()}
          >
            <Plus size={16} />
            {createPromotion.isPending ? 'Creating...' : 'Create promotion'}
          </button>
        </div>
      </div>

      <div className="table-wrap">
        <table className="crm-table">
          <thead>
            <tr>
              <th>Promotion</th>
              <th>Value</th>
              <th>Window</th>
              <th>Redemptions</th>
              <th>Status</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {promotions.isLoading ? (
              <tr>
                <td colSpan={6}>Loading promotions...</td>
              </tr>
            ) : (promotions.data ?? []).length ? (
              promotions.data!.map((promotion) => (
                <tr key={promotion.id}>
                  <td>
                    <strong>{promotion.display_name}</strong>
                    <span>{promotion.code}</span>
                  </td>
                  <td>{summarizePromotion(promotion)}</td>
                  <td>
                    <span>
                      {promotion.starts_at ? new Date(promotion.starts_at).toLocaleDateString() : 'Now'} -{' '}
                      {promotion.ends_at ? new Date(promotion.ends_at).toLocaleDateString() : 'Open'}
                    </span>
                  </td>
                  <td>
                    {promotion.redemption_count}
                    {promotion.max_redemptions != null ? ` / ${promotion.max_redemptions}` : ''}
                  </td>
                  <td>
                    <span className={`pill ${promotion.is_active ? 'pill-medium' : 'pill-soft'}`}>
                      {promotion.is_active ? 'Active' : 'Archived'}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="button button-secondary"
                      disabled={!promotion.is_active || archivePromotion.isPending}
                      onClick={() => archivePromotion.mutate(promotion.id)}
                    >
                      <Archive size={16} />
                      Archive
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6}>No promotions yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
